/**
 * User settings page — CoordioCore form-page-pattern.
 *
 * Layout: PageFormLayout (ShellHeader + SectionSideNav grid + panel sections)
 * Fields: @coordio/ui FormField (label + ⓘ tooltip + required asterisk)
 * Cards:  @coordio/ui FormSection (title + description + bordered body)
 * Toggle: ui-form-choice-field CSS classes (label + Switch in horizontal row)
 */
import { useEffect, useMemo, useState } from 'react';
import { Globe, Bell } from 'lucide-react';

import { CoordioDropdown, FormField, Input, Switch } from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

import { PageFormLayout } from '../components/layouts/PageFormLayout';
import { settingsService } from '../services/settingsService';
import { useNotificationStore } from '../store/notificationStore';
import { useSettingsStore } from '../store/settingsStore';
import type { UpdateUserSettingsRequest, UserSettingsDto } from '../types/settings';

/**
 * User settings page.
 */
export const UserSettingsPage = () => {
  const { addNotification } = useNotificationStore();
  const { updateSettings: updateUiSettings } = useSettingsStore();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<UserSettingsDto | null>(null);

  const initialForm = useMemo<UpdateUserSettingsRequest>(
    () => ({
      theme: 'system',
      language: 'en',
      timeZone: 'UTC',
      dateFormat: 'MM/dd/yyyy',
      timeFormat: '12h',
      emailNotificationsEnabled: true,
      pushNotificationsEnabled: true,
      notificationPreferencesJson: null,
      dashboardLayoutJson: null,
      preferencesJson: null,
    }),
    []
  );

  const [form, setForm] = useState<UpdateUserSettingsRequest>(initialForm);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const settings = await settingsService.getMyUserSettings();
      setData(settings);
      setForm({
        theme: settings.theme || 'system',
        language: settings.language || 'en',
        timeZone: settings.timeZone || 'UTC',
        dateFormat: settings.dateFormat || 'MM/dd/yyyy',
        timeFormat: settings.timeFormat || '12h',
        emailNotificationsEnabled: settings.emailNotificationsEnabled,
        pushNotificationsEnabled: settings.pushNotificationsEnabled,
        notificationPreferencesJson: settings.notificationPreferencesJson ?? null,
        dashboardLayoutJson: settings.dashboardLayoutJson ?? null,
        preferencesJson: settings.preferencesJson ?? null,
      });

      if (settings.theme === 'light' || settings.theme === 'dark' || settings.theme === 'system') {
        updateUiSettings({ mode: settings.theme });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load user settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await settingsService.updateMyUserSettings(form);
      setData(updated);
      addNotification('User settings saved', 'success');

      if (updated.theme === 'light' || updated.theme === 'dark' || updated.theme === 'system') {
        updateUiSettings({ mode: updated.theme });
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to save user settings';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageFormLayout
      title="User Settings"
      subtitle={data?.userId ? `User: ${data.userId}` : undefined}
      data-testid="user-settings-form-page"
      loading={loading}
      error={error ?? undefined}
      onErrorDismiss={() => setError(null)}
      actions={{
        onSave: save,
        onCancel: () => window.history.back(),
        saveLabel: 'Save',
        saving,
      }}
      navGroups={[
        {
          title: 'Preferences',
          items: [
            { id: 'display-locale', label: 'Display & Locale', icon: <Globe size={14} /> },
            { id: 'notifications', label: 'Notifications', icon: <Bell size={14} /> },
          ],
        },
      ]}
      sections={[
        {
          id: 'display-locale',
          title: 'Display & Locale',
          description: 'Theme, language, time zone, and date/time formatting preferences.',
          icon: <Globe size={16} />,
          content: (
            <FormSection title="Display preferences" description="Customize the visual appearance and regional formatting." data-testid="user-settings-section-display-locale">
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField label="Theme" description="Controls the overall color scheme of the interface." data-testid="user-settings-theme-field">
                  <CoordioDropdown
                    options={[
                      { value: 'system', label: 'System' },
                      { value: 'light', label: 'Light' },
                      { value: 'dark', label: 'Dark' },
                    ]}
                    value={form.theme}
                    onChange={(val) => setForm((s) => ({ ...s, theme: val ?? '' }))}
                    placeholder="Select theme"
                    clearable={false}
                    data-testid="user-settings-theme"
                  />
                </FormField>

                <FormField label="Language" description="Preferred display language." data-testid="user-settings-language-field">
                  <Input
                    className="w-full"
                    value={form.language}
                    onChange={(e) => setForm((s) => ({ ...s, language: e.target.value }))}
                    disabled={loading}
                    data-testid="user-settings-language"
                  />
                </FormField>

                <FormField label="Time Zone" description="Time zone used for displaying dates and scheduling." data-testid="user-settings-timeZone-field">
                  <Input
                    className="w-full"
                    value={form.timeZone}
                    onChange={(e) => setForm((s) => ({ ...s, timeZone: e.target.value }))}
                    disabled={loading}
                    data-testid="user-settings-timeZone"
                  />
                </FormField>

                <FormField label="Date Format" description='Pattern for displaying dates, e.g. "MM/dd/yyyy".' data-testid="user-settings-dateFormat-field">
                  <Input
                    className="w-full"
                    value={form.dateFormat}
                    onChange={(e) => setForm((s) => ({ ...s, dateFormat: e.target.value }))}
                    disabled={loading}
                    data-testid="user-settings-dateFormat"
                  />
                </FormField>

                <FormField label="Time Format" description="12-hour or 24-hour clock display." data-testid="user-settings-timeFormat-field">
                  <CoordioDropdown
                    options={[
                      { value: '12h', label: '12-hour' },
                      { value: '24h', label: '24-hour' },
                    ]}
                    value={form.timeFormat}
                    onChange={(val) => setForm((s) => ({ ...s, timeFormat: val ?? '' }))}
                    placeholder="Select time format"
                    clearable={false}
                    data-testid="user-settings-timeFormat"
                  />
                </FormField>
              </div>
            </FormSection>
          ),
        },
        {
          id: 'notifications',
          title: 'Notifications',
          description: 'Control which notification channels are active.',
          icon: <Bell size={16} />,
          content: (
            <FormSection title="Notification channels" description="Enable or disable notification delivery methods." data-testid="user-settings-section-notifications">
              <div className="flex flex-col gap-4">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch
                        checked={form.emailNotificationsEnabled}
                        onCheckedChange={(checked) => setForm((s) => ({ ...s, emailNotificationsEnabled: checked }))}
                        data-testid="user-settings-emailNotifications"
                      />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Email notifications</span>
                    </div>
                  </div>
                </div>

                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch
                        checked={form.pushNotificationsEnabled}
                        onCheckedChange={(checked) => setForm((s) => ({ ...s, pushNotificationsEnabled: checked }))}
                        data-testid="user-settings-pushNotifications"
                      />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Push notifications</span>
                    </div>
                  </div>
                </div>
              </div>
            </FormSection>
          ),
        },
      ]}
    />
  );
};
